import { AlertCircle, CheckCircle, Info, Loader2 } from 'lucide-react'
import { FormItem, Tooltip } from 'vnetwork-ui'
import { TFunction } from 'i18next'
import { CustomerEmailExistsResponse } from '@/modules/Customer/services'

interface StepEmailProps {
    control: any
    emailChecking: boolean
    emailValid: boolean
    emailValue?: string
    emailExistsResult?: CustomerEmailExistsResponse | null
    t: TFunction
}

interface ExistsMessage {
    text: string
    /** Nếu có → hiện icon (i) sau message, hover ra nội dung hướng dẫn */
    tooltip?: string
}

/** Build specific error messages based on where email exists */
const getExistsMessages = (
    result: CustomerEmailExistsResponse,
    t: TFunction
): ExistsMessage[] => {
    const msgs: ExistsMessage[] = []
    if (result.exists_in_customers)
        msgs.push({ text: t('email-exists-in-customers') })
    if (result.exists_in_customer_contacts)
        msgs.push({ text: t('email-exists-in-customer-contacts') })
    if (result.exists_in_contact_sources)
        msgs.push({
            text: t('email-exists-in-contact-sources'),
            tooltip: t('email-exists-in-contact-sources-tooltip'),
        })
    if (msgs.length === 0) msgs.push({ text: t('email-already-exists') })
    return msgs
}

/**
 * Step 1: Email validation
 */
const StepEmail = ({
    control,
    emailChecking,
    emailValid,
    emailValue,
    emailExistsResult,
    t,
}: StepEmailProps) => {
    const isEmailFormat =
        !!emailValue && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)
    const showError =
        !emailChecking && !emailValid && isEmailFormat && !!emailExistsResult

    return (
        <div data-tour="email-input" className="w-full">
            <div className="relative">
                <FormItem
                    control={control}
                    name="customer.customer_email"
                    type="textfield"
                    label="Email *"
                    placeholder="example@company.com"
                    className="relative"
                />
                {/* Status icon inside input */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    {emailChecking && (
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    )}
                    {!emailChecking && emailValid && emailValue && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                    {showError && (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                </div>
            </div>
            {emailValid && emailValue && (
                <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
                    {t('email-valid')}
                </p>
            )}
            {showError && (
                <div className="mt-1 space-y-0.5 ps-2">
                    {getExistsMessages(emailExistsResult!, t).map((msg) => (
                        <p
                            key={msg.text}
                            className="text-red-500 text-xs flex items-center gap-1"
                        >
                            {msg.text}
                            {msg.tooltip && (
                                <Tooltip title={msg.tooltip}>
                                    <Info className="w-3.5 h-3.5 text-red-400 cursor-help" />
                                </Tooltip>
                            )}
                        </p>
                    ))}
                </div>
            )}
        </div>
    )
}

export default StepEmail
